import type { CachedState, InvitedAppShortcutsCache } from '../types';

const queue: Array<() => void> = [];

/**
 * Enqueues a functional update to the cache. The update will be applied after all
 * previously enqueued updates have been processed.
 *
 * Going through this function ensures that multiple updates to the cache are applied sequentially,
 * preventing race conditions.
 *
 * @param cache - The cache to update
 * @param updater - A function that returns a new state to be written to the cache.
 *   It's called with `prev`, which is the latest cache state.
 *   If it returns `null` or `prev`, the cache will not be updated.
 *
 * @returns A promise that resolves to the new cached state after the update has been applied.
 */
export const enqueueUpdate = (
	cache: InvitedAppShortcutsCache,
	updater: (prev: CachedState) => CachedState | null,
): Promise<CachedState> => {
	let resolve: (result: CachedState) => void;
	let reject: (result: unknown) => void;
	const promise = new Promise<CachedState>((res, rej) => {
		resolve = res;
		reject = rej;
	});
	const processUpdate = async () => {
		const prev = cache.read({ ssr: false }) ?? {};
		try {
			const result = updater(prev);
			if (result === null || result === prev) {
				resolve(prev);
			} else {
				await cache.write(result);
				resolve(result);
			}
		} catch (error) {
			reject(error);
		}
		queue.shift(); // Remove the processed update
		if (queue.length > 0) {
			const nextProcessUpdate = queue[0];
			nextProcessUpdate!(); // Start the next update, keep it in the queue (blocking) until processed
		}
	};
	queue.push(processUpdate);
	if (queue.length === 1) {
		processUpdate();
	}
	return promise;
};
